// src/components/Approach.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, useMotionValue, useTransform } from 'framer-motion';

const Section = styled.section`
    padding: 120px 0;
    position: relative;
    overflow: hidden;
`;

const AboutSection = styled(Section)`
    background: linear-gradient(135deg,
    #ffffff 0%,
    #fafbff 50%,
    #ffffff 100%);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg,
        transparent 0%,
        rgba(102, 126, 234, 0.2) 50%,
        transparent 100%);
    }
`;

const BackgroundPattern = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.02;
    background-image:
            radial-gradient(circle at 20% 30%, #667eea 1px, transparent 0),
            radial-gradient(circle at 80% 70%, #764ba2 1px, transparent 0);
    background-size: 80px 80px;
`;

const SectionHeader = styled.div`
    text-align: center;
    max-width: 800px;
    margin: 0 auto 6rem;
    padding: 0 2rem;
`;

const SectionTitle = styled(motion.h2)`
    font-size: 4rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, #1a1a1a 0%, #667eea 50%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.1;
    position: relative;

    @media (max-width: 1200px) {
        font-size: 3.5rem;
    }

    @media (max-width: 768px) {
        font-size: 2.8rem;
    }

    @media (max-width: 480px) {
        font-size: 2.2rem;
    }
`;

const TitleUnderline = styled(motion.div)`
    width: 100px;
    height: 4px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 2px;
    margin: 0 auto 2rem;
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1.4rem;
    color: #4a5568;
    line-height: 1.7;
    font-weight: 400;
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.8);
`;

const TimelineContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 3rem;
    position: relative;

    @media (max-width: 768px) {
        padding: 0 1.5rem;
    }
`;

const Timeline = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 3px;
        height: 100%;
        background: linear-gradient(to bottom,
        transparent 0%,
        #667eea 15%,
        #764ba2 85%,
        transparent 100%);
        border-radius: 3px;

        @media (max-width: 968px) {
            display: none;
        }
    }

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 2rem;
    }
`;

const TimelineItem = styled(motion.div)`
    position: relative;
    
    &:nth-child(odd) {
        margin-top: 4rem;
        
        @media (max-width: 968px) {
            margin-top: 0;
        }
    }
    
    &:nth-child(even) {
        margin-top: 8rem;
        
        @media (max-width: 968px) {
            margin-top: 0;
        }
    }
`;
const TimelineCard = styled(motion.div)`
    background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.95) 100%);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 24px;
    padding: 3rem;
    position: relative;
    box-shadow:
            0 20px 40px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(255, 255, 255, 0.6) inset,
            0 4px 20px rgba(102, 126, 234, 0.1) inset;
    transition: all 0.4s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: visible; /* CHANGED: was 'hidden' → now 'visible' */

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 24px 24px 0 0;
    }

    /* Rest of your styles... */
`;

const TimelineIcon = styled(motion.div)`
    width: 80px;
    height: 80px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
    position: relative;
    background: linear-gradient(135deg, #667eea, #764ba2);
    box-shadow: 0 15px 30px rgba(102, 126, 234, 0.3);
    
    &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 22px;
        z-index: -1;
        opacity: 0.6;
        filter: blur(10px);
    }
    
    svg {
        width: 36px;
        height: 36px;
        color: white;
    }
`;

const TimelineNumber = styled(motion.div)`
    position: absolute;
    top: -20px;           /* Slightly higher for better visibility */
    right: -20px;         /* Slightly more offset */
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 800;
    font-size: 1.3rem;
    box-shadow:
            0 10px 25px rgba(102, 126, 234, 0.5),
            0 0 0 6px rgba(255, 255, 255, 0.9),   /* White halo */
            0 0 30px rgba(102, 126, 234, 0.4);
    z-index: 10;          /* Force it on top */
    border: 4px solid white;  /* Clean white border */
    pointer-events: none; /* Prevents hover interference */

    /* Ensure it's visible above all layers */
    transform: translateZ(0);
    backface-visibility: hidden;
`;

const TimelineTitle = styled.h3`
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1a1a1a;
    line-height: 1.3;
    
    @media (max-width: 768px) {
        font-size: 1.5rem;
    }
`;

const TimelineDescription = styled.p`
    color: #4a5568;
    line-height: 1.7;
    font-size: 1.1rem;
    font-weight: 400;
    flex-grow: 1;
`;

const FloatingOrbs = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
`;

const Orb = styled(motion.div)`
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
    filter: blur(20px);
`;

// SVG Icons for each approach point
const Icons = {
    safety: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
    ),
    tailored: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
    ),
    expertise: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
        </svg>
    ),
    seamless: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
        </svg>
    )
};

const Approach: React.FC = () => {
    const [hoveredCard, setHoveredCard] = useState<number | null>(null);

    const timelineData = [
        {
            title: "Comfort & Safety First",
            description: "Modern vehicles and professional drivers ensuring your journey is as enjoyable as the destination. Advanced safety features and meticulous maintenance for complete peace of mind.",
            icon: Icons.safety,
            number: "01"
        },
        {
            title: "Tailored Experiences",
            description: "Completely custom tours designed around your unique interests. Whether cultural immersion, historical exploration, or natural wonders, we create your perfect itinerary.",
            icon: Icons.tailored,
            number: "02"
        },
        {
            title: "Local Expertise",
            description: "Deep knowledge of Central Europe's hidden gems and must-see attractions. Our guides share authentic insights and exclusive access to create unforgettable moments.",
            icon: Icons.expertise,
            number: "03"
        },
        {
            title: "Seamless Service",
            description: "From airport transfers to multi-day tours, we handle every detail with precision and care. 24/7 support and concierge services for a truly effortless experience.",
            icon: Icons.seamless,
            number: "04"
        }
    ];

    return (
        <AboutSection id="approach">
            <BackgroundPattern />

            <FloatingOrbs>
                {[...Array(8)].map((_, i) => (
                    <Orb
                        key={i}
                        animate={{
                            y: [0, -30, 0],
                            x: [0, Math.random() * 40 - 20, 0],
                            scale: [1, 1.2, 1],
                        }}
                        transition={{
                            duration: 5 + Math.random() * 3,
                            repeat: Infinity,
                            delay: Math.random() * 2,
                        }}
                        style={{
                            width: Math.random() * 120 + 60,
                            height: Math.random() * 120 + 60,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </FloatingOrbs>

            <SectionHeader>
                <SectionTitle
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    viewport={{ once: true, margin: "-100px" }}
                >
                    Our Premium Approach
                </SectionTitle>

                <TitleUnderline
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    viewport={{ once: true }}
                />

                <SectionSubtitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                >
                    Redefining travel in Central Europe with innovative experiences,
                    unparalleled service and attention to every detail
                </SectionSubtitle>
            </SectionHeader>

            <TimelineContainer>
                <Timeline>
                    {timelineData.map((item, index) => (
                        <TimelineItem
                            key={index}
                            initial={{ opacity: 0, y: 60 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.8,
                                delay: index * 0.15,
                                ease: "easeOut"
                            }}
                            viewport={{ once: true, margin: "-50px" }}
                            onHoverStart={() => setHoveredCard(index)}
                            onHoverEnd={() => setHoveredCard(null)}
                        >
                            <TimelineCard
                                whileHover={{
                                    scale: 1.03,
                                    transition: { duration: 0.3 }
                                }}
                            >
                                <TimelineNumber
                                    initial={{ scale: 0, rotate: -180 }}
                                    whileInView={{ scale: 1, rotate: 0 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: index * 0.15 + 0.3,
                                        type: "spring"
                                    }}
                                    viewport={{ once: true }}
                                >
                                    {item.number}
                                </TimelineNumber>

                                <TimelineIcon
                                    initial={{ scale: 0, rotate: -180 }}
                                    whileInView={{ scale: 1, rotate: 0 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: index * 0.15 + 0.2,
                                        type: "spring"
                                    }}
                                    viewport={{ once: true }}
                                    whileHover={{
                                        scale: 1.1,
                                        rotate: 5,
                                        transition: { duration: 0.3 }
                                    }}
                                >
                                    {item.icon}
                                </TimelineIcon>

                                <TimelineTitle>{item.title}</TimelineTitle>
                                <TimelineDescription>{item.description}</TimelineDescription>
                            </TimelineCard>
                        </TimelineItem>
                    ))}
                </Timeline>
            </TimelineContainer>
        </AboutSection>
    );
};

export default Approach;